/**
 * Lead Capture Step Component
 *
 * Gated form to collect project and customer information before showing results.
 * This step is required before the estimate can be calculated and displayed.
 */

import React from 'react';
import { useEstimator } from '../../../context/EstimatorContext';
import './Steps.css';

export const LeadCaptureStep: React.FC = () => {
  const { state, updateProjectInfo } = useEstimator();
  const { projectInfo } = state;

  const handleChange = (field: keyof typeof projectInfo, value: string) => {
    updateProjectInfo({ [field]: value });
  };

  // Basic email validation
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const emailError = projectInfo.email && !isValidEmail(projectInfo.email);

  return (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">Project Information</h2>
        <p className="step-description">
          Enter your project details to generate your material estimate
        </p>
      </div>

      <div className="lead-capture-form">
        {/* Project Name */}
        <div className="form-section">
          <div className="lead-field">
            <label htmlFor="projectName" className="lead-label">
              Project Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="projectName"
              className="lead-input"
              value={projectInfo.projectName}
              onChange={(e) => handleChange('projectName', e.target.value)}
              placeholder="e.g., Main Garage Renovation"
              required
            />
          </div>
        </div>

        {/* Customer Name */}
        <div className="form-section">
          <div className="lead-field">
            <label htmlFor="customerName" className="lead-label">
              Your Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="customerName"
              className="lead-input"
              value={projectInfo.customerName}
              onChange={(e) => handleChange('customerName', e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>
        </div>

        {/* Email */}
        <div className="form-section">
          <div className="lead-field">
            <label htmlFor="email" className="lead-label">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              className={`lead-input ${emailError ? 'error' : ''}`}
              value={projectInfo.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="you@example.com"
              required
            />
            {emailError && (
              <span className="field-error">Please enter a valid email address</span>
            )}
          </div>
        </div>

        {/* Phone (Optional) */}
        <div className="form-section">
          <div className="lead-field">
            <label htmlFor="phone" className="lead-label">
              Phone Number <span className="optional">(optional)</span>
            </label>
            <input
              type="tel"
              id="phone"
              className="lead-input"
              value={projectInfo.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        {/* Location (Optional) */}
        <div className="form-section">
          <div className="lead-field">
            <label htmlFor="location" className="lead-label">
              Location <span className="optional">(optional)</span>
            </label>
            <input
              type="text"
              id="location"
              className="lead-input"
              value={projectInfo.location || ''}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="City, State/Province"
            />
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="alert alert-tc-info d-flex align-items-start gap-3 mt-4" role="alert">
        <div className="flex-shrink-0 text-info">
          <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div>
          <strong>Your information is secure</strong>
          <p className="mb-0 small mt-1">
            We use your contact information to save your estimate and connect you with local retailers.
            Your data is never shared without your consent.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureStep;
